export const name = '$gzSystemDown';

/**
 * @ngdoc object
 * @name gazelle.core.systemDown.provider:$gzSystemDownProvider
 * @description Allows for configuring the system down `$http` interceptor,
 *   for handling 5xx server responses.
 */
export default function $gzSystemDownProvider() {
  // By default, the alert dialog is only used in production.
  let alertDialogEnabled = !__DEV__;
  // By default, alert toast is only used in development.
  let alertToastEnabled = __DEV__;

  /**
   * @ngdoc property
   * @type {string[]}
   * @name essentialServices
   * @propertyOf gazelle.core.systemDown.provider:$gzSystemDownProvider
   * @description Contains service names that are considered essential.
   *   Any service unavailable (503) errors received in an non-developer build
   *   will cause an alert popup to be displayed.
   *   **Note:** AAA and portal are considered essential services for all apps.
   * @example
   * ```javascript
     function configEssentialServices($gzSystemDownProvider) {
       'ngInject';
       $gzSystemDownProvider.essentialServices.push('PrintbeatService');
     }
   * ```
   */
  let essentialServices = this.essentialServices = ['aaa', 'portal_service'];
  this.useAlertDialog = useAlertDialog;
  this.useAlertToast = useAlertToast;
  this.$get = $gzSystemDown;

  return;

  /**
   * @ngdoc function
   * @name useAlertDialog
   * @methodOf gazelle.core.systemDown.provider:$gzSystemDownProvider
   * @param {boolean} enableDialog true to enable the dialog, false to disable it
   * @description Allows for configuring if the alert dialog will be used for 503 errors.
   *   By default, this is false for development builds and true for production builds
   *   (using the webpack `__DEV__` flag).
   */
  function useAlertDialog(enableDialog) {
    alertDialogEnabled = enableDialog;
  }

  /**
   * @ngdoc function
   * @name useAlertToast
   * @methodOf gazelle.core.systemDown.provider:$gzSystemDownProvider
   * @param {boolean} enableToast true to enable developer toasts, false to disable them
   * @description Allows for configuring if alert toasts will be used for 5xx errors.
   *   By default, this is true for development builds and false for production builds
   *   (using the webpack `__DEV__` flag).
   */
  function useAlertToast(enableToast) {
    alertToastEnabled = enableToast;
  }

  function $gzSystemDown($log, $q, $injector, $rootScope, $translate) {
    'ngInject';

    // $log.debug('gzSystemDown loaded.');
    let processing5xx = false;

    let service = {
      responseError: responseError,
    };

    return service;

    /////////////////

    function responseError(response) {
      // Whoops! We got a 5xx from the server
      // Only do this when not in development mode.
      if ((response.status >= 502 && response.status <= 504) && alertDialogEnabled) {
        // $log.debug('gzSystemDown responseError interceptor running');

        isEssentialService(response)
          .then(() => {
            // We got a bad gateway[502], service unavailable[503] or a gateway timeout[504] (apache is up, but tomcats are not)
            if (!processing5xx) {
              $rootScope.inErrorState = response;
              processing5xx = true;
              $log.debug('response = ', response);
              $log.debug(`Oh no!!! We got a ${response.status}.`);
              $log.debug(`Response = ${response.statusText} for ${response.config.url}`);
              let rnApplication = $injector.get('rnApplication');
              show5xxError(response)
                .then(function () {
                  rnApplication.rebootApp();
                })
                .catch(function () {
                  rnApplication.rebootApp(true);
                })
                .finally(function () {
                  processing5xx = false;
                });
            }
          })
          .catch(() => {
            // swallow - don't care about non-essential services
          });
      }
      // Only do this in development mode.
      if (response.status >= 500 && response.status <= 599 && alertToastEnabled) {
        $rootScope.inErrorState = response;
        let $mdToast = $injector.get('$mdToast');
        $mdToast.show(
          $mdToast.simple({
            content: `${response.statusText} (${response.status})`,
          })
        );
      }
      return $q.reject(response);
    }

    /**
     * @param url to match against an essential service
     * @returns {Promise.<TResult>} resolved if the URL is from an essentials service, rejected otherwise
       */
    function isEssentialService(response) {
      // Check for an explicit service name. Important to determine if the portal service is offline.
      let explicitServiceName = _.get(response, 'config.rhino.serviceName');
      if(explicitServiceName) {
        if(_.includes(essentialServices, explicitServiceName)) {
          return $q.resolve();
        }
        return $q.reject();
      }

      let rnRegistry = $injector.get('rnRegistry');
      return rnRegistry.getServiceName(response.config.url)
        .then(serviceName => {
          if(_.includes(essentialServices, serviceName)) {
            return $q.resolve();
          }
          return $q.reject();
        });
    }

    function show5xxError(response) {
      let $gzAlertDialog = $injector.get('$gzAlertDialog');
      let content = translateDefault('uxf.uxf.SystemDown_content', 'There was a problem accessing the requested page.');
      return $gzAlertDialog.show(
        {
          title: `${response.statusText} (${response.status})`,
          content: content,
          cancel: translateDefault('uxf.uxf.SystemDown_GoHome', 'PrintOS Home'),
          ok: translateDefault('uxf.uxf.SystemDown_TryAgain', 'Try Again'),
        }
      );
    }

    /**
     * Attempts to translate a string, falling back to a default (like the `translate-default` directive).
     * TODO: Consider moving this to gazelle l10n as an angular-translate decorator like angular-promise extras.
     * See: https://github.com/ohjames/angular-promise-extras/blob/master/angular-promise-extras.js
     * See: http://www.bennadel.com/blog/2870-using-module-decorator-in-angularjs-1-4.htm
     *
     * @param locKey {string} localization key
     * @param defaultString {string} fallback (English) translation
     * @returns {string} translation or defaultString if translation not found
     */
    function translateDefault(locKey, defaultString) {
      let translation = $translate.instant(locKey);
      if (translation === locKey) {
        // Missing translation (perhaps because service down).  Return default string.
        return defaultString;
      }
      return translation;
    }

  }
}
